import { useState, useMemo } from 'react';
import { AlertCircle, ChevronRight, Clock, DollarSign, Package } from 'lucide-react';
import { Flag, CountrySelect } from '../../components/Flag';
import { MONTH_NAMES } from '../../config/months';

const getRslMonths = (expDate) => {
  if (!expDate) return -999;
  const today = new Date();
  const exp = new Date(expDate + 'T00:00:00Z');
  return (exp - today) / (1000 * 60 * 60 * 24 * 30.44);
};

const getRisk = (rsl) => rsl < 6 ? 'critical' : rsl < 12 ? 'warning' : 'ok';
const RISK_COLORS = {
  critical: { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', dot: 'bg-red-500', bar: 'bg-red-500', badge: 'bg-red-100 text-red-700' },
  warning: { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700', dot: 'bg-orange-500', bar: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700' },
  ok: { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', dot: 'bg-green-500', bar: 'bg-green-500', badge: 'bg-green-100 text-green-700' },
};
const RISK_LABELS = { critical: 'Critical', warning: 'At Risk', ok: 'OK' };

const computeSkus = (rawSkus) => rawSkus.map(sku => {
  const allBatches = sku.batches.map(b => ({ ...b, rslMonths: parseFloat(getRslMonths(b.expDate).toFixed(1)) }));
  const batches = allBatches.filter(b => b.rslMonths >= 10);
  if (batches.length === 0) return null;
  const minRSL = Math.min(...batches.map(b => b.rslMonths));
  const totalUnits = batches.reduce((s, b) => s + b.units, 0);
  const totalUsd = batches.reduce((s, b) => s + b.usd, 0);
  return { ...sku, batches, minRSL, totalUnits, totalUsd, risk: getRisk(minRSL) };
}).filter(Boolean);

export const InventoryModule = ({ inventoryData }) => {
  // Always start at LATAM on entry (no last-selection restore).
  const [selectedCountry, setSelectedCountryRaw] = useState('latam');
  const setSelectedCountry = (v) => { setSelectedCountryRaw(v); setExpandedCards({}); };
  const [expandedCards, setExpandedCards] = useState({});

  const isNewFormat = inventoryData && typeof inventoryData === 'object' && !Array.isArray(inventoryData);
  const countries = useMemo(() => isNewFormat ? Object.keys(inventoryData).sort((a, b) => (inventoryData[a]?.name || a).localeCompare(inventoryData[b]?.name || b)) : [], [inventoryData, isNewFormat]);
  const activeCountry = selectedCountry === 'latam' ? 'latam' : (countries.includes(selectedCountry) ? selectedCountry : 'latam');

  // Compute stats per country for LATAM summary
  const countryStats = useMemo(() => {
    const stats = {};
    countries.forEach(c => {
      const skus = computeSkus(inventoryData[c]?.skus || []);
      stats[c] = {
        name: inventoryData[c]?.name || c,
        skuCount: skus.length,
        criticalCount: skus.filter(s => s.risk === 'critical').length,
        warningCount: skus.filter(s => s.risk === 'critical' || s.risk === 'warning').length,
        totalUnits: skus.reduce((s, sk) => s + sk.totalUnits, 0),
        totalUsd: skus.reduce((s, sk) => s + sk.totalUsd, 0),
      };
    });
    return stats;
  }, [countries, inventoryData]);

  // Current country SKUs
  const countryData = isNewFormat ? inventoryData?.[activeCountry] : null;
  const rawSkus = countryData?.skus || [];
  const skus = useMemo(() => computeSkus(rawSkus), [rawSkus]);

  const criticalCount = skus.filter(s => s.risk === 'critical').length;
  const warningCount = skus.filter(s => s.risk === 'critical' || s.risk === 'warning').length;
  const totalUnits = skus.reduce((s, sk) => s + sk.totalUnits, 0);
  const totalUsd = skus.reduce((s, sk) => s + sk.totalUsd, 0);

  if (!isNewFormat || countries.length === 0) {
    return (
      <div className="space-y-6">
        <div><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-1">Supply &amp; shelf life</p><h2 className="text-2xl sm:text-[28px] leading-tight font-bold text-slate-900 tracking-tight">Inventory &amp; RSL</h2><p className="text-sm text-slate-500 mt-1">Invoice-level inventory with remaining shelf life tracking</p></div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-lg font-semibold text-slate-700">No Inventory Data Available</p>
          <p className="text-sm text-slate-500 mt-1">Upload the Inventory & RSL Excel file from the Admin panel.</p>
        </div>
      </div>
    );
  }

  const formatDate = (d) => {
    if (!d) return '—';
    const [y, m, day] = d.split('-');
    return `${MONTH_NAMES[parseInt(m)-1]} ${parseInt(day)}, ${y}`;
  };

  const formatNum = (v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v.toString();

  // LATAM summary totals
  const latamCritical = Object.values(countryStats).reduce((s, c) => s + c.criticalCount, 0);
  const latamWarning = Object.values(countryStats).reduce((s, c) => s + c.warningCount, 0);
  const latamUnits = Object.values(countryStats).reduce((s, c) => s + c.totalUnits, 0);
  const latamUsd = Object.values(countryStats).reduce((s, c) => s + c.totalUsd, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-1">Supply &amp; shelf life</p><h2 className="text-2xl sm:text-[28px] leading-tight font-bold text-slate-900 tracking-tight">Inventory &amp; RSL</h2><p className="text-sm text-slate-500 mt-1">Invoice-level inventory with remaining shelf life tracking</p></div>
      </div>

      {/* Country filter */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 flex items-center gap-4">
        <span className="text-sm text-slate-600">Country:</span>
        <CountrySelect
          value={activeCountry}
          onChange={setSelectedCountry}
          options={[
            { value: 'latam', label: 'LATAM (All Countries)', country: 'latam' },
            ...countries.map(c => ({ value: c, label: inventoryData[c]?.name || c, country: c })),
          ]}
        />
        <span className="text-xs text-slate-400 ml-auto">RSL calculated as of today</span>
      </div>

      {activeCountry === 'latam' ? (
        /* ============ LATAM SUMMARY VIEW ============ */
        <>
          {/* LATAM KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-red-50 to-white rounded-lg shadow-sm border border-red-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-slate-600">Critical Items</span><AlertCircle className="w-5 h-5 text-red-600" /></div>
              <div className="text-2xl font-bold text-slate-800">{latamCritical}</div>
              <div className="text-xs text-red-600 mt-1">RSL &lt; 6 months</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-lg shadow-sm border border-orange-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-slate-600">Short Life</span><Clock className="w-5 h-5 text-orange-600" /></div>
              <div className="text-2xl font-bold text-slate-800">{latamWarning}</div>
              <div className="text-xs text-orange-600 mt-1">RSL &lt; 12 months</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg shadow-sm border border-blue-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-slate-600">Total Stock</span><Package className="w-5 h-5 text-blue-600" /></div>
              <div className="text-2xl font-bold text-slate-800">{formatNum(latamUnits)}</div>
              <div className="text-xs text-slate-600 mt-1">units across all markets</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-white rounded-lg shadow-sm border border-green-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-slate-600">Total Value</span><DollarSign className="w-5 h-5 text-green-600" /></div>
              <div className="text-2xl font-bold text-slate-800">${formatNum(latamUsd)}</div>
              <div className="text-xs text-green-600 mt-1">USD across all markets</div>
            </div>
          </div>

          {/* Country summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {countries.map(c => {
              const st = countryStats[c];
              return (
                <button key={c} onClick={() => setSelectedCountry(c)}
                  className="text-left bg-white rounded-lg shadow-sm border border-slate-200 p-5 hover:shadow-md hover:border-blue-300 transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Flag country={c} size={20} />
                      <h3 className="font-bold text-slate-800 text-lg">{st.name}</h3>
                    </div>
                    <span className="text-xs text-slate-400">{st.skuCount} SKUs</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="rounded-lg p-3 bg-slate-50">
                      <div className="flex items-center gap-1.5 mb-1"><AlertCircle className="w-3.5 h-3.5 text-slate-400" /><span className="text-xs font-medium text-slate-500">Critical</span></div>
                      <span className={`text-xl font-bold ${st.criticalCount > 0 ? 'text-red-600' : 'text-slate-300'}`}>{st.criticalCount}</span>
                    </div>
                    <div className="rounded-lg p-3 bg-slate-50">
                      <div className="flex items-center gap-1.5 mb-1"><Clock className="w-3.5 h-3.5 text-slate-400" /><span className="text-xs font-medium text-slate-500">Short Life</span></div>
                      <span className={`text-xl font-bold ${st.warningCount > 0 ? 'text-orange-500' : 'text-slate-300'}`}>{st.warningCount}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{formatNum(st.totalUnits)} units</span>
                    <span>${formatNum(st.totalUsd)} USD</span>
                    <span className="text-blue-600 font-medium">View details →</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        /* ============ COUNTRY DETAIL VIEW ============ */
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-red-50 to-white rounded-lg shadow-sm border border-red-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-slate-600">Critical Items</span><AlertCircle className="w-5 h-5 text-red-600" /></div>
              <div className="text-2xl font-bold text-slate-800">{criticalCount}</div>
              <div className="text-xs text-red-600 mt-1">RSL &lt; 6 months</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-lg shadow-sm border border-orange-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-slate-600">Short Life</span><Clock className="w-5 h-5 text-orange-600" /></div>
              <div className="text-2xl font-bold text-slate-800">{warningCount}</div>
              <div className="text-xs text-orange-600 mt-1">RSL &lt; 12 months</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg shadow-sm border border-blue-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-slate-600">Total Stock</span><Package className="w-5 h-5 text-blue-600" /></div>
              <div className="text-2xl font-bold text-slate-800">{formatNum(totalUnits)}</div>
              <div className="text-xs text-slate-600 mt-1">units</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-white rounded-lg shadow-sm border border-green-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-slate-600">Total Value</span><DollarSign className="w-5 h-5 text-green-600" /></div>
              <div className="text-2xl font-bold text-slate-800">${formatNum(totalUsd)}</div>
              <div className="text-xs text-green-600 mt-1">USD</div>
            </div>
          </div>

          {/* SKU cards */}
          <div className="space-y-3">
            {skus.map((sku, idx) => {
              const colors = RISK_COLORS[sku.risk];
              const isExpanded = expandedCards[idx];
              const mostCriticalBatch = sku.batches.length > 0 ? sku.batches.reduce((min, b) => b.rslMonths < min.rslMonths ? b : min, sku.batches[0]) : null;
              return (
                <div key={sku.name} className={`${colors.bg} border-l-4 ${colors.border} rounded-lg shadow-sm`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-slate-800 text-sm">{sku.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colors.badge}`}>{RISK_LABELS[sku.risk]}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>{sku.totalUnits.toLocaleString()} units</span>
                          <span>${sku.totalUsd.toLocaleString()} USD</span>
                          <span>{sku.batches.length} batch{sku.batches.length !== 1 ? 'es' : ''}</span>
                        </div>
                      </div>
                      <Package className={`w-5 h-5 ${colors.text}`} />
                    </div>

                    {mostCriticalBatch && (
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-slate-600">Min RSL — Batch {mostCriticalBatch.batch}</span>
                          <span className={`text-sm font-bold ${colors.text}`}>{sku.minRSL.toFixed(1)} mo</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                          <div className={`h-2 rounded-full ${colors.bar}`} style={{ width: `${Math.max(0, Math.min((sku.minRSL / 24) * 100, 100))}%` }}></div>
                        </div>
                        {sku.minRSL >= 0 && sku.minRSL < 6 && <p className="text-xs text-red-600 font-medium mt-1">Approaching expiry — action required</p>}
                      </div>
                    )}

                    <button onClick={() => setExpandedCards(prev => ({ ...prev, [idx]: !prev[idx] }))}
                      className="w-full flex items-center justify-between px-3 py-2 bg-white/80 hover:bg-white border border-slate-200 rounded-lg text-sm">
                      <div className="flex items-center gap-2"><Package className="w-3.5 h-3.5 text-slate-500" /><span className="font-medium text-slate-700">Batch Details ({sku.batches.length})</span></div>
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4">
                      <div className="bg-white/80 rounded-lg border border-slate-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left p-2 text-xs font-medium text-slate-600">Batch</th>
                              <th className="text-right p-2 text-xs font-medium text-slate-600">Units</th>
                              <th className="text-right p-2 text-xs font-medium text-slate-600">USD</th>
                              <th className="text-left p-2 text-xs font-medium text-slate-600">Exp Date</th>
                              <th className="text-right p-2 text-xs font-medium text-slate-600">RSL</th>
                              <th className="p-2 text-xs font-medium text-slate-600 w-32"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {sku.batches.sort((a, b) => a.rslMonths - b.rslMonths).map((b, bi) => {
                              const bRisk = getRisk(b.rslMonths);
                              const bc = RISK_COLORS[bRisk];
                              return (
                                <tr key={bi} className="border-t border-slate-100">
                                  <td className="p-2 text-xs font-medium text-slate-800">{b.batch}</td>
                                  <td className="p-2 text-xs text-right text-slate-700">{b.units.toLocaleString()}</td>
                                  <td className="p-2 text-xs text-right text-slate-700">${b.usd.toLocaleString()}</td>
                                  <td className="p-2 text-xs text-slate-600">{formatDate(b.expDate)}</td>
                                  <td className={`p-2 text-xs text-right font-bold ${bc.text}`}>{b.rslMonths.toFixed(1)} mo</td>
                                  <td className="p-2"><div className="w-full bg-slate-200 rounded-full h-1.5"><div className={`h-1.5 rounded-full ${bc.bar}`} style={{ width: `${Math.max(0, Math.min((b.rslMonths / 24) * 100, 100))}%` }}></div></div></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
